import { Icon } from '@iconify/react';
import { useState, useRef, useEffect } from 'react';
import {
  Button,
  Input,
  Divider,
  Chip,
  Textarea,
  Tabs,
  Tab,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@nextui-org/react';
import { Modal, ModalContent, ModalHeader, ModalBody } from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { format } from 'date-fns';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { HistoryItem } from 'common/src/utils/types';
import {
  getSettlementHistory,
  addHistoryComment,
  updateHistoryComment,
  deleteHistoryItem,
  addHistoryDocument,
  uploadDocumentFile,
  getDocumentUrl,
} from '../../services/historyService';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

type ExcelPreviewSheet = {
  name: string;
  rows: string[][];
  totalRows: number;
  totalColumns: number;
};

type DocumentPreview =
  | {
      kind: 'pdf' | 'other';
      fileName: string;
      url: string;
    }
  | {
      kind: 'excel';
      fileName: string;
      url: string;
      sheets: ExcelPreviewSheet[];
    };

const EXCEL_PREVIEW_MAX_ROWS = 200;
const EXCEL_PREVIEW_MAX_COLUMNS = 50;

interface SettlementHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  settlement: Settlement | null;
  onChange?: () => void;
}

const SettlementHistoryDrawer = ({
  isOpen,
  onClose,
  settlement,
  onChange,
}: SettlementHistoryDrawerProps) => {
  const { userProfile } = useUserStore();
  const [newComment, setNewComment] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [selectedTab, setSelectedTab] = useState('comments');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [documentPreview, setDocumentPreview] =
    useState<DocumentPreview | null>(null);
  const [activePreviewSheet, setActivePreviewSheet] = useState(0);
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (isOpen && settlement?.id && userProfile?.id) {
      loadHistory();
    }
  }, [isOpen, settlement?.id, userProfile?.id]);

  const loadHistory = async () => {
    if (!settlement?.id) return;
    setIsLoading(true);
    try {
      const history = await getSettlementHistory(settlement.id);
      setHistoryItems(history);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setIsLoading(false);
    }
  };

  if (!settlement || !userProfile) return null;

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const comment = await addHistoryComment(
        settlement.id,
        newComment,
        userProfile.id
      );
      setHistoryItems([comment, ...historyItems]);
      setNewComment('');
      onChange?.();
      toast.success('Comentario agregado');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Error al agregar el comentario');
    }
  };

  const handleEditComment = async (id: number, newText: string) => {
    try {
      const updatedComment = await updateHistoryComment(id, newText);
      setHistoryItems((items) =>
        items.map((item) => (item.id === id ? updatedComment : item))
      );
      setEditingCommentId(null);
      setEditingContent('');
      toast.success('Comentario actualizado');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Error al actualizar el comentario');
    }
  };

  const startEditing = (comment: HistoryItem) => {
    setEditingCommentId(comment.id);
    setEditingContent(comment.content || '');
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditingContent('');
  };

  const handleDeleteHistoryItem = async (id: number) => {
    try {
      await deleteHistoryItem(id);
      setHistoryItems((items) => items.filter((item) => item.id !== id));
      onChange?.();
      toast.success('Elemento eliminado');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Error al eliminar el elemento');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setDocumentTitle(file.name.split('.').slice(0, -1).join('.')); // Remove extension
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setDocumentTitle(file.name.split('.').slice(0, -1).join('.')); // Remove extension
    }
  };

  const handleAddDocument = async () => {
    if (!documentTitle.trim() || !selectedFile || !userProfile.id) return;

    setIsUploadingDocument(true);
    try {
      // Generate a unique path for the file
      const fileExt = selectedFile.name.split('.').pop();
      const filePath = `${settlement.id}/${Date.now()}-${Math.random()
        .toString(36)
        .substring(7)}.${fileExt}`;

      // Upload the file
      await uploadDocumentFile(selectedFile, filePath);

      // Create the document record
      const document = await addHistoryDocument(
        settlement.id,
        documentTitle,
        selectedFile.name,
        filePath,
        userProfile.id
      );

      setHistoryItems([document, ...historyItems]);
      setDocumentTitle('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onChange?.();
      toast.success('Documento agregado');
    } catch (error) {
      console.error('Error adding document:', error);
      toast.error('Error al agregar el documento');
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const getDocumentExtension = (fileName: string, filePath: string) =>
    (fileName || filePath).split('?')[0].split('.').pop()?.toLowerCase() || '';

  const handlePreviewDocument = async (
    documentId: number,
    filePath: string,
    fileName: string,
    bucket = 'settlements_documents'
  ) => {
    setPreviewLoadingId(documentId);
    try {
      const url = await getDocumentUrl(filePath, bucket);
      if (!url) throw new Error('Could not create a signed document URL');

      const extension = getDocumentExtension(fileName, filePath);
      if (extension === 'pdf') {
        setDocumentPreview({ kind: 'pdf', fileName, url });
        setActivePreviewSheet(0);
        return;
      }

      if (extension === 'xls' || extension === 'xlsx') {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Could not load document (${response.status})`);
        }

        const workbook = XLSX.read(await response.arrayBuffer(), {
          type: 'array',
          cellDates: true,
        });
        const sheets = workbook.SheetNames.map((name) => {
          const rawRows = XLSX.utils.sheet_to_json<unknown[]>(
            workbook.Sheets[name],
            {
              header: 1,
              defval: '',
              raw: false,
            }
          );
          const totalColumns = rawRows.reduce(
            (maximum, row) =>
              Math.max(maximum, Array.isArray(row) ? row.length : 0),
            0
          );
          const rows = rawRows
            .slice(0, EXCEL_PREVIEW_MAX_ROWS)
            .map((row) =>
              (Array.isArray(row) ? row : [])
                .slice(0, EXCEL_PREVIEW_MAX_COLUMNS)
                .map((cell) => String(cell ?? ''))
            );

          return {
            name,
            rows,
            totalRows: rawRows.length,
            totalColumns,
          };
        });

        setDocumentPreview({ kind: 'excel', fileName, url, sheets });
        setActivePreviewSheet(0);
        return;
      }

      setDocumentPreview({ kind: 'other', fileName, url });
    } catch (error) {
      console.error('Error previewing document:', error);
      toast.error('Error al previsualizar el documento');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleDownloadDocument = async (
    documentId: number,
    filePath: string,
    fileName: string,
    bucket = 'settlements_documents'
  ) => {
    setDownloadLoadingId(documentId);
    try {
      const url = await getDocumentUrl(filePath, bucket);
      if (!url) throw new Error('Could not create a signed document URL');

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Could not download document (${response.status})`);
      }

      const objectUrl = URL.createObjectURL(await response.blob());
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || filePath.split('/').pop() || 'documento';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Error al descargar el documento');
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const comments = historyItems.filter(
    (item) => item.type === 'comment' || item.type === 'action'
  );
  const documents = historyItems.filter((item) => item.type === 'document');

  const isItemOwner = (createdById: number) => {
    return createdById === userProfile?.id;
  };

  const previewSheet =
    documentPreview?.kind === 'excel'
      ? documentPreview.sheets[activePreviewSheet]
      : null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        hideCloseButton
        classNames={{
          wrapper: '!items-stretch !justify-end',
          base: '!m-0 h-[100dvh] !rounded-l-large !rounded-r-none data-[state=open]:!duration-200 !w-full sm:!max-w-[700px]',
          backdrop: 'bg-[rgba(0,0,0,0.5)]',
        }}
      >
        <ModalContent className='h-full flex flex-col'>
          {(closeDrawer) => (
            <>
              <ModalHeader className='flex flex-col gap-1 border-b-1 border-default-100'>
                <div className='flex justify-between items-center'>
                  <div className='flex flex-col gap-1'>
                    <h3 className='text-xl font-bold'>
                      Bitácora de Liquidación
                    </h3>
                    <span className='font-semibold'>
                      Ref {settlement.reference_number}
                    </span>
                  </div>
                  <Button isIconOnly variant='light' onPress={closeDrawer}>
                    <Icon icon='tabler:x' />
                  </Button>
                </div>
              </ModalHeader>
              <Tabs
                selectedKey={selectedTab}
                onSelectionChange={(key) => setSelectedTab(key as string)}
                className='w-full px-6 pt-4'
                variant='underlined'
                classNames={{
                  tabList: 'gap-6',
                  cursor: 'bg-brand-900',
                  tab: 'max-w-fit px-0 h-12',
                  tabContent: 'group-data-[selected=true]:text-brand-900',
                }}
              >
                <Tab
                  key='comments'
                  title={
                    <div className='flex items-center gap-2'>
                      <Icon
                        icon='tabler:message'
                        className={`text-xl ${
                          selectedTab === 'comments'
                            ? 'text-brand-900'
                            : 'text-default-400'
                        }`}
                      />
                      <span>Comentarios</span>
                    </div>
                  }
                />
                <Tab
                  key='documents'
                  title={
                    <div className='flex items-center gap-2'>
                      <Icon
                        icon='tabler:file-text'
                        className={`text-xl ${
                          selectedTab === 'documents'
                            ? 'text-brand-900'
                            : 'text-default-400'
                        }`}
                      />
                      <span>Documentos</span>
                      {documents.length > 0 && (
                        <Chip
                          size='sm'
                          variant='flat'
                          className={`${
                            selectedTab === 'documents'
                              ? 'bg-brand-100 text-brand-700'
                              : 'bg-default-100 text-default-500'
                          }`}
                        >
                          {documents.length}
                        </Chip>
                      )}
                    </div>
                  }
                />
              </Tabs>
              <ModalBody className='flex-grow overflow-y-auto py-6'>
                {isLoading ? (
                  <div className='flex flex-col items-center justify-center h-full gap-3'>
                    <div className='animate-spin rounded-full h-10 w-10 border-4 border-brand-700 border-t-transparent'></div>
                    <p className='text-default-600'>Cargando bitácora...</p>
                  </div>
                ) : (
                  <>
                    {selectedTab === 'comments' && (
                      <div className='space-y-4'>
                        <div className='sticky top-0 bg-background pb-4 z-10'>
                          <div className='flex items-start gap-3'>
                            <div className='w-8 h-8 rounded-full bg-primary flex items-center justify-center text-sm font-medium text-primary-foreground'>
                              {userProfile.first_name?.[0]}
                              {userProfile.last_name?.[0]}
                            </div>
                            <div className='flex-1'>
                              <Textarea
                                placeholder='Escribir un comentario...'
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                className='w-full'
                                minRows={2}
                                classNames={{
                                  base: 'max-w-full focus-within:shadow-sm transition-shadow',
                                  input: 'resize-none text-sm',
                                  inputWrapper:
                                    'bg-background border border-default-200 hover:border-default-400 rounded-lg px-3 py-2.5',
                                }}
                              />
                              <div
                                className={`mt-2 flex items-center gap-2 ${
                                  newComment.trim()
                                    ? 'opacity-100'
                                    : 'opacity-0 hidden'
                                } transition-opacity`}
                              >
                                <Button
                                  size='sm'
                                  color='primary'
                                  className='text-sm px-3 min-w-[80px]'
                                  onPress={handleAddComment}
                                  isDisabled={!newComment.trim()}
                                >
                                  Guardar
                                </Button>
                                <Button
                                  size='sm'
                                  variant='light'
                                  className='text-sm px-3'
                                  onPress={() => setNewComment('')}
                                >
                                  Cancelar
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <Divider className='my-2' />

                        {comments.map((comment) => (
                          <div
                            key={comment.id}
                            className='flex items-start gap-3 group'
                          >
                            {comment.type === 'action' ? (
                              <div className='w-full flex items-center gap-2 py-1.5'>
                                <div className='w-8 h-8 rounded-full bg-default-100 flex items-center justify-center'>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='w-4 h-4 text-default-600'
                                    viewBox='0 0 20 20'
                                    fill='currentColor'
                                  >
                                    <path
                                      fillRule='evenodd'
                                      d='M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z'
                                      clipRule='evenodd'
                                    />
                                  </svg>
                                </div>
                                <div className='flex-1'>
                                  <span className='text-sm text-default-600'>
                                    {comment.content}
                                  </span>
                                  <div className='mt-0.5'>
                                    <span className='text-xs text-default-400'>
                                      {format(
                                        new Date(comment.created_at),
                                        "d MMM' a las 'HH:mm"
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className='w-8 h-8 rounded-full bg-primary flex items-center justify-center text-sm font-medium text-primary-foreground'>
                                  {comment.created_by.first_name?.[0]}
                                  {comment.created_by.last_name?.[0]}
                                </div>
                                <div className='flex-1 min-w-0'>
                                  <div className='flex items-baseline gap-2 flex-wrap'>
                                    <span className='text-sm text-default-700'>
                                      {comment.created_by.first_name}{' '}
                                      {comment.created_by.last_name}
                                    </span>
                                    <span className='text-default-500 text-xs'>
                                      {format(
                                        new Date(comment.created_at),
                                        "d MMM' a las 'HH:mm"
                                      )}
                                    </span>
                                    {comment.is_edited && (
                                      <span className='text-default-400 italic text-xs'>
                                        (editado)
                                      </span>
                                    )}
                                  </div>

                                  {editingCommentId === comment.id ? (
                                    <div className='mt-2 space-y-2'>
                                      <Textarea
                                        value={editingContent}
                                        onChange={(e) =>
                                          setEditingContent(e.target.value)
                                        }
                                        className='w-full'
                                        minRows={2}
                                        classNames={{
                                          input: 'resize-none text-sm',
                                          inputWrapper:
                                            'bg-background border border-default-200 hover:border-default-400 rounded-lg px-3 py-2.5',
                                        }}
                                      />
                                      <div className='flex gap-2'>
                                        <Button
                                          size='sm'
                                          color='primary'
                                          className='text-sm px-3'
                                          onPress={() =>
                                            handleEditComment(
                                              comment.id,
                                              editingContent
                                            )
                                          }
                                          isDisabled={!editingContent.trim()}
                                        >
                                          Guardar
                                        </Button>
                                        <Button
                                          size='sm'
                                          variant='light'
                                          className='text-sm px-3'
                                          onPress={cancelEditing}
                                        >
                                          Cancelar
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className='mt-1 text-sm text-default-600'>
                                      {comment.content}
                                    </div>
                                  )}
                                </div>

                                <div>
                                  <Dropdown>
                                    <DropdownTrigger>
                                      <Button
                                        isIconOnly
                                        variant='light'
                                        size='sm'
                                      >
                                        <Icon icon='tabler:dots-vertical' className='text-default-500' />
                                      </Button>
                                    </DropdownTrigger>
                                    <DropdownMenu>
                                      <DropdownItem
                                        startContent={<Icon icon='tabler:pencil' />}
                                        onPress={() => startEditing(comment)}
                                      >
                                        Editar
                                      </DropdownItem>
                                      <DropdownItem
                                        className='text-danger'
                                        color='danger'
                                        startContent={<Icon icon='tabler:trash' />}
                                        onPress={() =>
                                          handleDeleteHistoryItem(comment.id)
                                        }
                                      >
                                        Eliminar
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </Dropdown>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedTab === 'documents' && (
                      <div className='space-y-4'>
                        {!selectedFile ? (
                          <div
                            className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
                              isDragging
                                ? 'border-brand-700 bg-brand-50'
                                : 'border-default-200 hover:border-brand-700 hover:bg-default-50'
                            }`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <input
                              type='file'
                              ref={fileInputRef}
                              onChange={handleFileSelect}
                              className='hidden'
                            />
                            <Icon icon='tabler:cloud-upload' width={48} className='mx-auto mb-4 text-default-400' />
                            <p className='text-lg text-default-600'>
                              Arrastra un archivo aquí o{' '}
                              <span className='text-brand-700 font-medium cursor-pointer hover:underline'>
                                selecciona uno
                              </span>
                            </p>
                            <p className='text-sm text-default-400 mt-2'>
                              PDF, Word, Excel o imágenes
                            </p>
                          </div>
                        ) : (
                          <div className='bg-default-50 p-4 rounded-xl'>
                            <div className='flex items-start gap-4'>
                              <div className='p-2 bg-background rounded-lg shadow-sm'>
                                <Icon icon='tabler:file-text' className='text-2xl text-brand-700' />
                              </div>
                              <div className='flex-1 space-y-1'>
                                <Input
                                  value={documentTitle}
                                  onChange={(e) =>
                                    setDocumentTitle(e.target.value)
                                  }
                                  variant='bordered'
                                  placeholder='Título del documento'
                                  className='flex-1'
                                  size='sm'
                                />
                                <p className='text-xs text-default-500'>
                                  Archivo: {selectedFile.name}
                                </p>
                              </div>
                              <div className='flex gap-2'>
                                <Button
                                  size='sm'
                                  color='primary'
                                  className='font-medium'
                                  isDisabled={
                                    !documentTitle.trim() || isUploadingDocument
                                  }
                                  onPress={handleAddDocument}
                                >
                                  {isUploadingDocument ? (
                                    <div className='flex items-center gap-2'>
                                      <div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent'></div>
                                      <span>Subiendo...</span>
                                    </div>
                                  ) : (
                                    'Adjuntar'
                                  )}
                                </Button>
                                <Button
                                  variant='light'
                                  isIconOnly
                                  size='sm'
                                  onPress={() => {
                                    setSelectedFile(null);
                                    setDocumentTitle('');
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = '';
                                    }
                                  }}
                                >
                                  <Icon icon='tabler:x' className='text-xl' />
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {documents.map((doc) => (
                          <div
                            key={doc.id}
                            className='flex min-w-0 flex-col gap-3 rounded-medium border border-default-200 p-4 transition-colors hover:border-brand-200 sm:flex-row sm:items-center'
                          >
                            <div className='min-w-0 flex-1'>
                              <div className='flex items-center gap-2'>
                                <p className='break-words font-medium text-default-700'>
                                  {doc.title}
                                </p>
                              </div>
                              <div className='mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
                                <p className='text-xs text-default-400'>
                                  Subido por {doc.created_by.first_name}{' '}
                                  {doc.created_by.last_name} el{' '}
                                  {format(
                                    new Date(doc.created_at),
                                    "dd/MM/yyyy' - 'HH:mm"
                                  )}
                                </p>
                                {doc.file_name && (
                                  <>
                                    <span className='text-xs text-default-300'>
                                      •
                                    </span>
                                    <p
                                      className='min-w-0 break-all text-xs text-default-500'
                                      title={doc.file_name}
                                    >
                                      {doc.file_name}
                                    </p>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className='flex shrink-0 items-center gap-2 self-end sm:self-center'>
                              <Button
                                isIconOnly
                                size='sm'
                                variant='flat'
                                className='bg-default-100 text-default-700'
                                aria-label={`Previsualizar ${doc.file_name || doc.title}`}
                                title='Previsualizar'
                                isLoading={previewLoadingId === doc.id}
                                onPress={() =>
                                  doc.file_path &&
                                  handlePreviewDocument(
                                    doc.id,
                                    doc.file_path,
                                    doc.file_name || doc.title || 'Documento',
                                    doc.storage_bucket
                                  )
                                }
                              >
                                <Icon icon='tabler:eye' className='text-lg' />
                              </Button>
                              <Button
                                isIconOnly
                                size='sm'
                                variant='flat'
                                className='bg-default-100 text-default-700'
                                aria-label={`Descargar ${doc.file_name || doc.title}`}
                                title='Descargar'
                                isLoading={downloadLoadingId === doc.id}
                                onPress={() =>
                                  doc.file_path &&
                                  handleDownloadDocument(
                                    doc.id,
                                    doc.file_path,
                                    doc.file_name || doc.title || 'Documento',
                                    doc.storage_bucket
                                  )
                                }
                              >
                                <Icon icon='tabler:download' className='text-lg' />
                              </Button>
                              {isItemOwner(doc.created_by.id) &&
                                !doc.source_document_id && (
                                  <Dropdown>
                                    <DropdownTrigger>
                                      <Button
                                        isIconOnly
                                        variant='flat'
                                        className='bg-default-100'
                                        size='sm'
                                      >
                                        <Icon icon='tabler:dots-vertical' />
                                      </Button>
                                    </DropdownTrigger>
                                    <DropdownMenu>
                                      <DropdownItem
                                        className='text-danger'
                                        color='danger'
                                        startContent={<Icon icon='tabler:trash' />}
                                        onPress={() =>
                                          handleDeleteHistoryItem(doc.id)
                                        }
                                      >
                                        Eliminar
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </Dropdown>
                                )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={Boolean(documentPreview)}
        onClose={() => {
          setDocumentPreview(null);
          setActivePreviewSheet(0);
        }}
        size='5xl'
        scrollBehavior='inside'
        classNames={{ base: 'h-[92dvh] max-h-[92dvh]' }}
      >
        <ModalContent>
          <ModalHeader className='min-w-0 border-b border-default-200'>
            <div className='min-w-0'>
              <p className='truncate pr-8'>{documentPreview?.fileName}</p>
              <p className='text-xs font-normal text-default-500'>
                Vista previa del documento
              </p>
            </div>
          </ModalHeader>
          <ModalBody className='min-h-0 p-0'>
            {documentPreview?.kind === 'pdf' && (
              <iframe
                title={`Vista previa de ${documentPreview.fileName}`}
                src={documentPreview.url}
                // design-ignore: el contenido del documento no sigue el tema; necesita fondo blanco para leerse en modo oscuro
                className='min-h-0 flex-1 bg-white'
              />
            )}

            {documentPreview?.kind === 'excel' && (
              <div className='flex min-h-0 flex-1 flex-col'>
                <div className='flex shrink-0 items-center gap-2 overflow-x-auto border-b border-default-200 p-3'>
                  {documentPreview.sheets.map((sheet, index) => (
                    <Button
                      key={`${sheet.name}-${index}`}
                      size='sm'
                      variant={
                        activePreviewSheet === index ? 'solid' : 'bordered'
                      }
                      color={activePreviewSheet === index ? 'primary' : 'default'}
                      className='shrink-0'
                      onPress={() => setActivePreviewSheet(index)}
                    >
                      {sheet.name}
                    </Button>
                  ))}
                </div>

                {previewSheet && previewSheet.rows.length > 0 ? (
                  <>
                    <div className='min-h-0 flex-1 overflow-auto bg-default-50'>
                      <table className='min-w-full border-collapse bg-background text-xs'>
                        <tbody>
                          {previewSheet.rows.map((row, rowIndex) => (
                            <tr
                              key={rowIndex}
                              className={
                                rowIndex === 0
                                  ? 'sticky top-0 z-10 bg-default-100 font-semibold'
                                  : 'even:bg-default-50'
                              }
                            >
                              <th className='sticky left-0 z-20 border border-default-200 bg-default-100 px-2 py-1.5 text-right font-normal text-default-400'>
                                {rowIndex + 1}
                              </th>
                              {row.map((cell, cellIndex) => (
                                <td
                                  key={cellIndex}
                                  className='max-w-[320px] whitespace-nowrap border border-default-200 px-3 py-1.5 text-default-700'
                                  title={cell}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {(previewSheet.totalRows > EXCEL_PREVIEW_MAX_ROWS ||
                      previewSheet.totalColumns >
                        EXCEL_PREVIEW_MAX_COLUMNS) && (
                      <p className='shrink-0 border-t border-default-200 px-4 py-2 text-xs text-default-500'>
                        Vista limitada a las primeras {EXCEL_PREVIEW_MAX_ROWS}{' '}
                        filas y {EXCEL_PREVIEW_MAX_COLUMNS} columnas. Descarga
                        el archivo para verlo completo.
                      </p>
                    )}
                  </>
                ) : (
                  <div className='flex flex-1 items-center justify-center text-sm text-default-500'>
                    Esta hoja no contiene datos.
                  </div>
                )}
              </div>
            )}

            {documentPreview?.kind === 'other' && (
              <iframe
                title={`Vista previa de ${documentPreview.fileName}`}
                src={documentPreview.url}
                // design-ignore: el contenido del documento no sigue el tema; necesita fondo blanco para leerse en modo oscuro
                className='min-h-0 flex-1 bg-white'
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SettlementHistoryDrawer;
